import React, { useContext, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { PRIMARY_DARK } from '../../constants/constants';
import { useStore } from '../../store/store';
import { sectionAccentColor } from '../../shared/ui/editorAccents';
import { PANEL_HEADING_STYLE, underlinedPanelHeadingStyle } from '../../shared/ui/panelHeading';
import { TooltipsContext } from '../../contexts/TooltipsContext';
import { FormControlsPortalContext } from '../../contexts/FormControlsPortalContext';

/* Le registre de couleurs vit dans `src/shared/ui/editorAccents.js` : le
   web-editor reprend la même grammaire de sections, et une table en double
   aurait divergé au premier ajout. Réexporté ici, où il était importé. */
export { sectionAccentColor as getSectionAccentColor } from '../../shared/ui/editorAccents';

const NEUTRAL_TITLE_COLOR = 'rgba(11, 34, 59, 0.45)';

// Amène la section sous les yeux dans le panel, à la verticale seulement :
// scrollIntoView chercherait aussi à la ramener à l'horizontale, alors que le
// panel replié est hors écran le temps de se déplier. Le haut visible est le
// bas de l'entête collant, qui précède l'hôte des sections (RightSide.jsx).
const revealInPanel = (section) => {
  const scroller = section.closest('.menu_wrapper');
  if (!scroller) return;
  const header = section.parentElement?.previousElementSibling;
  const visibleTop = header
    ? header.getBoundingClientRect().bottom
    : scroller.getBoundingClientRect().top;
  const visibleBottom = scroller.getBoundingClientRect().bottom;
  const { top, bottom } = section.getBoundingClientRect();
  let delta = 0;
  if (top < visibleTop) delta = top - visibleTop;
  else if (bottom > visibleBottom) delta = Math.min(bottom - visibleBottom, top - visibleTop);
  if (delta) scroller.scrollBy({ top: delta, behavior: 'smooth' });
};

// Une section de l'inspecteur (panel de droite) pour la box sélectionnée.
// Chaque appel = un bloc vertical avec un entête discret (uppercase gris) et
// son contenu. Plus de cadre/bordure autour : les sections sont séparées par
// une fine ligne basse, comme dans une maquette d'inspecteur type Figma.
// layout='row' : contenu en barre horizontale qui peut wrap (boutons de
// style, étroits). layout='column' : contrôles empilés (switches, sélecteurs).
// order : les sections sont portées dans l'ordre de montage (variable selon le
// type de box) ; `order` fige leur position pour garder une place cohérente
// (ex. « Actions » toujours en haut). L'hôte est un flex column.
// keepOnMultiSelection : par défaut une section disparaît en multi-sélection
// (ctrl/cmd + clic en mode dessin). Les réglages du panel portent sur la box
// maîtresse seule — changer la couleur de fond avec trois box sélectionnées n'en
// touchait qu'une, et le panel affichait « Forme » alors que la sélection
// mélangeait forme et image. Seules les sections qui valent pour la sélection
// entière (Actions) ou pour le container/la section restent montées.
// flashNonce : fait clignoter la section (et la fait défiler jusqu'à l'écran)
// pour la désigner depuis le document. Chaque nouvelle valeur rejoue le repère ;
// `onFlashEnd` signale sa fin, pour que l'appelant repasse à null.
// hideFlashOverlay : garde le défilement et la temporisation (onFlashEnd
// toujours déclenché en fin d'animation), seul le voile bleu n'est pas rendu visible.
// neutralTitle : titre gris comme « Actions » sans être placé en tête (order
// < 0) — pour une section qui ne règle pas la box elle-même (ex. Disposition,
// qui règle son container).
const BoxFormControlsPortal = ({
  children,
  layout = 'column',
  title,
  order = 0,
  keepOnMultiSelection = false,
  flashNonce = null,
  onFlashEnd,
  hideFlashOverlay = false,
  neutralTitle = false,
}) => {
  const isMultiSelection = useStore(({ groupSelection }) => groupSelection.length > 0);
  const sectionRef = useRef(null);
  const isFlashing = flashNonce != null;

  useEffect(() => {
    if (flashNonce != null && sectionRef.current) revealInPanel(sectionRef.current);
  }, [flashNonce]);

  // Un éditeur posé par-dessus le panel de droite (modale de forme) fournit
  // son propre hôte : sans ça, les sections iraient derrière lui.
  const portalTarget = useContext(FormControlsPortalContext);
  const portalNode = portalTarget ?? document.getElementById('RightSideQuickEditPortal');
  if (!portalNode) return null;
  if (isMultiSelection && !keepOnMultiSelection) return null;

  return ReactDOM.createPortal(
    <div
      // Un portail reste un enfant React du composant qui l'a monté : les
      // handlers onClick/onMouseDown du container et de la box (attachés en
      // React, qui bubble sur l'arbre React et non le DOM) se déclenchent pour
      // un clic dans le panel de droite et désélectionnaient la box qu'on est
      // justement en train de régler. Le marqueur est posé ici, sur l'hôte de
      // toutes les sections, plutôt que sur chaque contrôle : le bug était
      // réapparu à chaque nouveau réglage ajouté au panel (toggles Affichage,
      // contrôles de container, switch organigramme).
      data-outside-ignore="true"
      ref={sectionRef}
      style={{
      // Seulement pendant le repère, qui se positionne sur la section : les
      // contrôles des sections gardent sinon leur référence de positionnement.
      ...(isFlashing ? { position: 'relative' } : {}),
      order,
      // Marge droite plus large : les contrôles pleine largeur (sélecteurs,
      // ligne police -/+) ne collent plus au bord droit du panel.
      padding: '14px 22px 14px 16px',
      borderBottom: '1px solid rgba(11, 34, 57, 0.08)',
      color: PRIMARY_DARK,
    }}>
      {title && (
        <div style={{
          // Un peu plus grand que le corps du panel (11px, cf. OverlaySider)
          // pour rester lisible comme séparateur de section.
          fontSize: 12,
          ...(order >= 0 && !neutralTitle
            ? underlinedPanelHeadingStyle(sectionAccentColor(title))
            : { ...PANEL_HEADING_STYLE, display: 'inline-block', color: NEUTRAL_TITLE_COLOR }),
          marginBottom: 12,
        }}>
          {title}
        </div>
      )}
      <div style={{
        display: 'flex',
        flexDirection: layout,
        flexWrap: layout === 'row' ? 'wrap' : 'nowrap',
        alignItems: layout === 'row' ? 'center' : 'stretch',
        gap: layout === 'row' ? '10px 8px' : 12,
      }}>
        {/* Pas d'infobulles dans le panneau (cf. TooltipsContext) ; posé ici,
            car les contrôles sont montés dans l'arbre de leur box, hors de
            RightSide : le contexte suit l'arbre React, pas le portail. */}
        <TooltipsContext.Provider value={false}>
          {children}
        </TooltipsContext.Provider>
      </div>
      {/* Voile inerte par-dessus la section (cf. .layout-focus-flash), en
          retrait pour rester dans le panel. La `key` rejoue l'animation à
          chaque demande, même si la précédente n'est pas terminée. */}
      {isFlashing && (
        <div
          key={flashNonce}
          className='layout-focus-flash'
          style={{ inset: 4, ...(hideFlashOverlay ? { opacity: 0 } : {}) }}
          aria-hidden='true'
          onAnimationEnd={onFlashEnd}
        />
      )}
    </div>,
    portalNode
  );
};

export default BoxFormControlsPortal;
